/**
 * Fuel monitoring utilities — smoothing, fill/drain detection, formatting.
 */

/**
 * Sliding-window median filter to remove fuel sloshing noise.
 * At edges, uses a smaller window (minimum 1).
 */
export function smoothFuelData(positions, windowSize = 5) {
  return positions.map((pos, i) => {
    const fuel = pos.attributes?.fuel;
    if (fuel == null) return { ...pos, _smoothedFuel: null };

    const halfW = Math.floor(windowSize / 2);
    const start = Math.max(0, i - halfW);
    const end = Math.min(positions.length - 1, i + halfW);
    const window = [];
    for (let j = start; j <= end; j++) {
      const f = positions[j].attributes?.fuel;
      if (f != null) window.push(f);
    }
    window.sort((a, b) => a - b);
    const median = window[Math.floor(window.length / 2)];
    return { ...pos, _smoothedFuel: median };
  });
}

/**
 * Detect fill and drain events from smoothed position data.
 * A fill is a sharp increase >= fillThreshold.
 * A drain is a sharp decrease >= drainThreshold.
 */
export function detectFuelEvents(
  smoothedPositions,
  { fillThreshold = 5, drainThreshold = 5 } = {}
) {
  const events = [];
  let i = 0;
  const pts = smoothedPositions.filter((p) => p._smoothedFuel != null);

  while (i < pts.length - 1) {
    const curr = pts[i]._smoothedFuel;
    const next = pts[i + 1]._smoothedFuel;
    const diff = next - curr;

    if (diff >= fillThreshold) {
      // Walk forward to find end of fill
      let endIdx = i + 1;
      while (
        endIdx < pts.length - 1 &&
        pts[endIdx + 1]._smoothedFuel - pts[endIdx]._smoothedFuel > 0
      ) {
        endIdx++;
      }
      events.push({
        type: 'fill',
        startIndex: i,
        endIndex: endIdx,
        startTime: pts[i].fixTime,
        endTime: pts[endIdx].fixTime,
        amount: Math.round((pts[endIdx]._smoothedFuel - curr) * 10) / 10,
        beforeLevel: curr,
        afterLevel: pts[endIdx]._smoothedFuel,
      });
      i = endIdx + 1;
    } else if (diff <= -drainThreshold) {
      // Walk forward to find end of drain
      let endIdx = i + 1;
      while (
        endIdx < pts.length - 1 &&
        pts[endIdx + 1]._smoothedFuel - pts[endIdx]._smoothedFuel < 0
      ) {
        endIdx++;
      }
      events.push({
        type: 'drain',
        startIndex: i,
        endIndex: endIdx,
        startTime: pts[i].fixTime,
        endTime: pts[endIdx].fixTime,
        amount: Math.round((curr - pts[endIdx]._smoothedFuel) * 10) / 10,
        beforeLevel: curr,
        afterLevel: pts[endIdx]._smoothedFuel,
      });
      i = endIdx + 1;
    } else {
      i++;
    }
  }

  return events;
}

/**
 * Read the fuel unit configured on a device.
 */
export function getFuelUnit(device) {
  return device?.attributes?.fuelUnit || 'percent';
}

/**
 * Format a fuel value with its unit.
 */
export function formatFuelValue(value, unit) {
  if (value == null) return '—';
  const rounded = Math.round(value * 10) / 10;
  if (unit === 'liters') return `${rounded} L`;
  if (unit === 'gallons') return `${rounded} gal`;
  return `${rounded}%`;
}

/**
 * Compute summary statistics from smoothed positions and detected events.
 */
export function computeFuelStats(smoothedPositions, fuelEvents) {
  const pts = smoothedPositions.filter((p) => p._smoothedFuel != null);
  if (pts.length === 0) return null;

  const levels = pts.map((p) => p._smoothedFuel);
  const avgLevel = Math.round((levels.reduce((s, v) => s + v, 0) / levels.length) * 10) / 10;
  const totalFilled = fuelEvents
    .filter((e) => e.type === 'fill')
    .reduce((s, e) => s + e.amount, 0);
  const totalDrained = fuelEvents
    .filter((e) => e.type === 'drain')
    .reduce((s, e) => s + e.amount, 0);
  // Total consumed = starting level + fills - ending level
  const startLevel = pts[0]._smoothedFuel;
  const endLevel = pts[pts.length - 1]._smoothedFuel;
  const totalConsumed = Math.round((startLevel + totalFilled - endLevel) * 10) / 10;

  return {
    avgLevel,
    totalConsumed: Math.max(0, totalConsumed),
    totalFilled: Math.round(totalFilled * 10) / 10,
    totalDrained: Math.round(totalDrained * 10) / 10,
  };
}
